import { localProductionOnly } from '../lib/localProduction'
import { ploteaApp, qaPanel } from '../lib/productProfile'
import { PloteaRail } from '../site/panels/PloteaRail'
import { SiteTopBar } from '../site/panels/SiteTopBar'
/**
 * Application shell based on shadcn's collapsible sidebar pattern.
 * Navigation owns the route hierarchy; the header exposes that hierarchy as
 * breadcrumbs while page components keep their actions and supporting copy.
 */
import { Fragment, type ReactNode } from 'react'
import { surroundingsPreviewUrl } from '../lib/surroundingsPreview'
import { SurroundingsPreviewProvider } from './SurroundingsPreviewContext'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { qk } from '../lib/queryKeys'
import { logout } from '../lib/endpoints/auth'
import { AppBreadcrumbs, AppBreadcrumbsProvider } from './AppBreadcrumbs'
import { AppSidebar } from './app-sidebar'
import { InvitationsBanner } from './InvitationsBanner'
import { SidebarInset, SidebarTrigger } from './ui/sidebar'
import { TooltipProvider } from './ui/tooltip'
import { RouteSidebarProvider } from './RouteSidebarProvider'

export function AppLayout({
  userName,
  isSystemAdmin,
  isProductAnalyticsReader,
  children,
}: {
  userName: string
  isSystemAdmin: boolean
  isProductAnalyticsReader: boolean
  children: ReactNode
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // The Site map has its own top bar (logo, search, account).
  const ownTopBar = useRouterState({
    select: (state) => state.location.pathname === '/site',
  })
  // Surroundings (and its `/surroundings/runs` poll) are Archigma-only: the
  // Plotea API host has no such endpoints.
  const PreviewProvider =
    surroundingsPreviewUrl && !localProductionOnly && !ploteaApp
      ? SurroundingsPreviewProvider
      : Fragment

  const handleLogout = () => {
    void logout().finally(() => {
      queryClient.removeQueries({ queryKey: qk.me() })
      void navigate({ to: '/login', replace: true })
    })
  }

  const handleSettings = () => {
    void navigate({ to: '/settings/api-keys' })
  }

  if (ploteaApp)
    return (
      <PloteaLayout userName={userName} mapPage={ownTopBar}>
        {children}
      </PloteaLayout>
    )

  return (
    <TooltipProvider>
      <PreviewProvider>
        <AppBreadcrumbsProvider>
          <RouteSidebarProvider>
            <AppSidebar
              userName={userName}
              isSystemAdmin={isSystemAdmin}
              isProductAnalyticsReader={isProductAnalyticsReader}
              onSettings={qaPanel ? undefined : handleSettings}
              onLogout={handleLogout}
            />
            <SidebarInset className="h-svh min-h-0 min-w-0 overflow-hidden">
              {!ownTopBar && (
                <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background px-4">
                  <SidebarTrigger className="-ml-1" />
                  <AppBreadcrumbs />
                </header>
              )}
              <SyncingBar />
              {!localProductionOnly && <InvitationsBanner />}
              <div className="flex-1 overflow-y-auto overscroll-none p-4 md:p-6">
                {children}
              </div>
            </SidebarInset>
          </RouteSidebarProvider>
        </AppBreadcrumbsProvider>
      </PreviewProvider>
    </TooltipProvider>
  )
}

/**
 * Plotea's shell: no app sidebar. The map page brings its own top bar and
 * module rail; the other pages get the same top bar and a rail with the way
 * back to the analysis and the other pages, so the app has one left rail.
 */
function PloteaLayout({
  userName,
  mapPage,
  children,
}: {
  userName: string
  mapPage: boolean
  children: ReactNode
}) {
  return (
    <TooltipProvider>
      <AppBreadcrumbsProvider>
        {/* Shared UI may still read the sidebar context; nothing opens it. */}
        <RouteSidebarProvider>
          <div className="flex h-svh min-h-0 w-full flex-col overflow-hidden bg-background">
            {!mapPage && (
              <SiteTopBar
                breadcrumbs={<AppBreadcrumbs />}
                search={null}
                userName={userName}
              />
            )}
            {!localProductionOnly && <InvitationsBanner />}
            <div className="flex min-h-0 flex-1">
              {!mapPage && <PloteaRail />}
              <main className="min-w-0 flex-1 overflow-y-auto overscroll-none p-4 md:p-6">
                {children}
              </main>
            </div>
          </div>
        </RouteSidebarProvider>
      </AppBreadcrumbsProvider>
    </TooltipProvider>
  )
}

/** Reserved for the background synchronization indicator. */
function SyncingBar() {
  return null
}
